import { WidgetBase } from './widgetBase';
import { AggregationConfiguration, IPeriodConfiguration, WidgetValuesReadConfiguration, ReadType, IBindable, AggregationMode, IOffset, RawInformation } from '../../models';
import { IwidgetIntervalHelperService } from '../../services/helpers';
import { IReadSeriesConfiguration, ISeriesConfiguration } from '../../widgets';

declare type SeriesRead = IBindable & IOffset & { rawInformation?: RawInformation };

export class DataWidgetBase extends WidgetBase {

    constructor(private _intervalHelper: IwidgetIntervalHelperService) {
        super();
    }

    getReadConfigurationBase(beginDate: string,
        endDate: string,
        series: IBindable[],
        configuration: IPeriodConfiguration,
        aggregation: AggregationConfiguration,
        hasQuality: boolean,
        hasContext: boolean,
        hasTendency: boolean,
        hasTarget: boolean,
        hasMissingRanges: boolean,
        readType?: ReadType,
        hasAlarm?: boolean,
        presentationType?: string,
        presentationUnit?: string
    ): WidgetValuesReadConfiguration {
        return this.getReadConfigurationBaseList(
            beginDate,
            endDate,
            series,
            configuration,
            aggregation,
            hasQuality,
            hasContext,
            hasTendency,
            hasTarget, hasMissingRanges,
            (readType !== null && readType !== undefined) ? [readType] : [],
            hasAlarm,
            presentationType,
            presentationUnit
        );
    }

    getReadConfigurationBaseList(beginDate: string,
        endDate: string,
        series: SeriesRead[],
        configuration: IPeriodConfiguration,
        aggregation: AggregationConfiguration,
        hasQuality: boolean,
        hasContext: boolean,
        hasTendency: boolean,
        hasTarget: boolean,
        hasMissingRanges: boolean,
        readTypes: ReadType[],
        hasAlarm?: boolean,
        presentationType?: string,
        presentationUnit?: string,
        showLogbook?: boolean
    ): WidgetValuesReadConfiguration {
        if (!readTypes) {
            readTypes = [];
        }
        const isAggregated = aggregation && aggregation.mode !== AggregationMode.None;
        if (readTypes.length === 0) {
            const readType = isAggregated ? ReadType.AggregatedValueList : ReadType.AllValues;
            readTypes.push(readType);
        }

        const seriesConfiguration: IReadSeriesConfiguration[] = this.mapSeries(series);

        const interval = this._intervalHelper.getInterval(beginDate, endDate, configuration);
        const result: WidgetValuesReadConfiguration = {
            beginDate: interval.beginMoment.toISOString(),
            endDate: interval.endMoment.toISOString(),
            series: seriesConfiguration,
            readTypes: readTypes,
            hasQuality: hasQuality,
            hasMissingRanges: hasMissingRanges,
            hasContext: hasContext,
            hasTendency: hasTendency,
            hasTarget: hasTarget,
            hasAlarm: !!hasAlarm,
            guid: this.guid,
            presentationType: presentationType,
            presentationUnit: presentationUnit,
            showLogbook: !!showLogbook
        };
        if (isAggregated) {
            result.aggregationMode = aggregation.mode;
            result.aggregationUnit = aggregation.period ? aggregation.period.unit : null;
            result.aggregationPeriod = aggregation.period ? aggregation.period.interval : null;
        }

        return result;
    }

    createConfiguration(
        bindables: SeriesRead[],
        aggregation: AggregationConfiguration,
        hasQuality: boolean,
        hasMissingRanges: boolean,
        hasContext: boolean,
        hasTendency: boolean,
        hasTarget: boolean,
        readType?: ReadType,
        presentationType?: string,
        presentationUnit?: string,
        showLogbook: boolean = false,
        hasAlarm: boolean = false
    ): WidgetValuesReadConfiguration {

        const isAggregated = aggregation && aggregation.mode !== AggregationMode.None;
        if (!readType && readType !== 0) {
            readType = isAggregated ? ReadType.AggregatedValueList : ReadType.AllValues;
        }
        const series = this.mapSeries(bindables);

        const result: WidgetValuesReadConfiguration = {
            series: series,
            readTypes: [readType],
            hasQuality: hasQuality,
            hasMissingRanges: hasMissingRanges,
            hasContext: hasContext,
            hasTendency: hasTendency,
            hasTarget: hasTarget,
            guid: this.guid,
            presentationType: presentationType,
            presentationUnit: presentationUnit,
            showLogbook: showLogbook,
            hasAlarm: hasAlarm
        };
        if (isAggregated) {
            result.aggregationMode = aggregation.mode;
            result.aggregationUnit = aggregation.period ? aggregation.period.unit : null;
            result.aggregationPeriod = aggregation.period ? aggregation.period.interval : null;
        }
        return result;
    }

    private mapSeries(series: SeriesRead[]): IReadSeriesConfiguration[] {
        const seriesConfiguration: IReadSeriesConfiguration[] = series
            ? series.map((d, i) => {
                const range: {
                    beginDate: string;
                    endDate: string;
                } = undefined;
                return {
                    guid: d.guid,
                    value: d.value,
                    isBoundToGroup: d.isBoundToGroup,
                    isBoundIncludehildren: d.isBoundIncludehildren,
                    // relativePath: d.relativePath
                    isQuery: d.isQuery,
                    id: i,
                    offset: d.offset,
                    range,
                    rawInformation: d.rawInformation
                };
            })
            : [];
        return seriesConfiguration;
    }

    getSeries(): ISeriesConfiguration[] {
        return this.configuration.series;
    }

    setSeries(series: ISeriesConfiguration[]) {
        this.configuration.series = series;
    }
}